import { useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "../stores/auth-store";
import { env } from "../lib/env";

const BASE_URL = env.API_BASE_URL;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CandidateCvResult {
  /** Object URL for the fetched CV blob. null while loading or on error. */
  blobUrl: string | null;
  /** MIME type returned by the server (e.g. "application/pdf", "application/msword"). */
  contentType: string | null;
  isLoading: boolean;
  /** True when the query failed (including 404). */
  isError: boolean;
  /** Triggers a browser download of the CV file. */
  download: (candidateName?: string) => void;
}

// ---------------------------------------------------------------------------
// Fetcher — returns blob + content-type so the hook can decide on rendering
// ---------------------------------------------------------------------------

async function fetchCv(
  candidateId: string,
  signal: AbortSignal
): Promise<{ blob: Blob; contentType: string }> {
  const { accessToken } = useAuthStore.getState();

  const headers = new Headers();
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  const response = await fetch(`${BASE_URL}/candidates/${candidateId}/cv`, {
    signal,
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    throw new Error(`CV fetch failed: ${response.status}`);
  }

  const blob = await response.blob();
  const contentType =
    response.headers.get("Content-Type") ?? "application/octet-stream";

  return { blob, contentType };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCandidateCv(
  candidateId: string | undefined
): CandidateCvResult {
  const query = useQuery({
    queryKey: ["candidate-cv", candidateId] as const,
    queryFn: ({ signal }) => fetchCv(candidateId!, signal),
    enabled: !!candidateId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Derive a stable object URL from the cached blob.
  // We use a ref to track the previous blob and its URL so that:
  //  1. We only create a new object URL when the blob reference actually changes.
  //  2. We never revoke the URL on unmount — React strict-mode double-mounting
  //     and tab-switch remounts would otherwise revoke the URL while react-pdf
  //     is still loading it. The underlying blob memory is managed by TanStack
  //     Query's gcTime, so the tiny URL string leak is harmless.
  const blobUrlRef = useRef<{ blob: Blob | undefined; url: string | null }>({
    blob: undefined,
    url: null,
  });

  const blobUrl = useMemo(() => {
    const currentBlob = query.data?.blob;
    if (currentBlob === blobUrlRef.current.blob) {
      return blobUrlRef.current.url;
    }
    // Blob changed — revoke the old URL and create a new one
    if (blobUrlRef.current.url) {
      URL.revokeObjectURL(blobUrlRef.current.url);
    }
    const url = currentBlob ? URL.createObjectURL(currentBlob) : null;
    blobUrlRef.current = { blob: currentBlob, url };
    return url;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data?.blob]);

  const download = (candidateName?: string) => {
    const blob = query.data?.blob;
    if (!blob) return;

    const contentType = query.data?.contentType ?? "application/octet-stream";
    const ext = contentType.includes("pdf")
      ? ".pdf"
      : contentType.includes("wordprocessingml") ||
          contentType.includes("vnd.openxmlformats")
        ? ".docx"
        : contentType.includes("msword")
          ? ".doc"
          : "";

    const filename = candidateName
      ? `${candidateName.replace(/\s+/g, "_")}_CV${ext}`
      : `CV${ext}`;

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return {
    blobUrl,
    contentType: query.data?.contentType ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    download,
  };
}
